// ─────────────────────────────────────────────────────────────────────────────
// LocationShippingExclusionService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for the shipping-exclusion Knowledge Base list.
// Endpoints: /v1/locations/shipping-exclusions (list, create),
// /v1/locations/shipping-exclusions/{location_id} (delete, idempotent),
// /v1/locations/shipping-exclusions/bulk (bulk create, idempotent per id),
// /v1/locations/shipping-exclusions/bulk/delete (bulk delete, idempotent
// per id) -- deliberately a DIFFERENT path from bulk create, not the same
// path with a different verb: Odoo's cors="*" OPTIONS responder answers
// per matched route, not per path, so two routes sharing one path each
// got a preflight response reflecting only ONE of their methods -- a
// DELETE preflight could come back allowing only POST, and the browser
// would silently refuse to send the real DELETE. Splitting the path
// sidesteps that ambiguity.
// No pagination -- this list is expected to stay small (a handful of
// main/billing-only locations per tenant).
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import { ICreateLocationShippingExclusionPayload, ILocationShippingExclusion } from '../models/location-shipping-exclusion.models';

/** Metadata on a bulk create response -- every requested id is exactly one
 * of created / duplicate (already-excluded or repeated in-request) / invalid
 * (doesn't resolve to a shipping-eligible location). */
export interface IBulkCreateLocationShippingExclusionMetadata {
  requested: number;
  created: number;
  duplicates: number;
  invalid: number;
}

/** Shaped like `APIResponse<ILocationShippingExclusion>` but with bulk-create
 * counts instead of pagination metadata -- kept as its own type rather than
 * `APIResponse<T> & {...}` since the two `metadata` shapes would otherwise
 * conflict. */
export interface IBulkCreateLocationShippingExclusionResponse {
  message: string;
  data: ILocationShippingExclusion[];
  object?: ILocationShippingExclusion;
  metadata: IBulkCreateLocationShippingExclusionMetadata;
}

@Injectable({
  providedIn: 'root',
})
export class LocationShippingExclusionService {
  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public list(): Observable<APIResponse<ILocationShippingExclusion>> {
    return this.http.get<APIResponse<ILocationShippingExclusion>>(`${environment.BASE_URL_API}locations/shipping-exclusions`);
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public create(payload: ICreateLocationShippingExclusionPayload): Observable<APIResponse<ILocationShippingExclusion>> {
    return this.http.post<APIResponse<ILocationShippingExclusion>>(`${environment.BASE_URL_API}locations/shipping-exclusions`, payload);
  }

  // ── Bulk create (idempotent per id -- ONE request for the whole batch) ──────

  public bulkCreate(locationIds: number[]): Observable<IBulkCreateLocationShippingExclusionResponse> {
    return this.http.post<IBulkCreateLocationShippingExclusionResponse>(
      `${environment.BASE_URL_API}locations/shipping-exclusions/bulk`,
      { location_ids: locationIds },
    );
  }

  // ── Delete (keyed by location_id, idempotent) ────────────────────────────────

  public remove(locationId: number): Observable<APIResponse<ILocationShippingExclusion>> {
    return this.http.delete<APIResponse<ILocationShippingExclusion>>(`${environment.BASE_URL_API}locations/shipping-exclusions/${locationId}`);
  }

  // ── Bulk delete (idempotent per id -- ONE request for the whole batch) ──────
  // `data` carries the deleted location_ids (numbers, not exclusion rows) --
  // the backend returns `result['deleted']`, a plain int list.

  public bulkRemove(locationIds: number[]): Observable<APIResponse<number>> {
    return this.http.delete<APIResponse<number>>(`${environment.BASE_URL_API}locations/shipping-exclusions/bulk/delete`, {
      body: { location_ids: locationIds },
    });
  }
}
